import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { of, take } from 'rxjs';
import { CrmObjectInjectionToken, FieldValueInterface, ObjectType } from '../../tokens-and-interfaces';
import { SNACKBAR_DURATION } from '../../constants';
import { CRMTrackingService } from '../../shared-services/page-analytics.service';
import { TranslateForCrmObjectService } from '../../i18n/translate-for-crm-object/translate-for-crm-object.service';
import { CrmFormService } from '../../shared-services/crm-services/crm-form.service';
import { CrmObjectManagementTableChangesService } from '../../shared-services';
import { switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class InlineEditService {
  private readonly crmFormService = inject(CrmFormService);
  private readonly snackService = inject(SnackbarService);
  private readonly trackingService = inject(CRMTrackingService);
  private readonly translateForCrmObjectService = inject(TranslateForCrmObjectService);
  private readonly crmObjectManagementTableChangesService = inject(CrmObjectManagementTableChangesService);
  private readonly crmObjectDependencies = inject(CrmObjectInjectionToken, { optional: true });

  private crmObjectId = '';
  setObjectId(objectId: string): void {
    this.crmObjectId = objectId;
  }
  getObjectId(): string {
    return this.crmObjectId;
  }
  private isInlineEdit = signal(false);
  readonly inlineEditEnabled = this.isInlineEdit.asReadonly();
  isInlineEdit$ = toObservable(this.isInlineEdit);
  setInlineEdit(inlineEdit: boolean): void {
    this.isInlineEdit.set(inlineEdit);
  }
  private objectSubtype$ = this.crmObjectDependencies?.objectSubtype$ ?? of('');

  saveInlineEdit(objectType: ObjectType, values: FieldValueInterface[]): void {
    if (!this.isInlineEdit()) {
      return;
    }

    this.objectSubtype$
      .pipe(
        switchMap((objectSubtype) => {
          return this.crmFormService
            .validateAndUpdateCrmObjectFields$(objectType, this.crmObjectId, values, objectSubtype)
            .pipe(take(1));
        }),
        withLatestFrom(
          this.translateForCrmObjectService.getTranslationForCrmObject(objectType, 'EDIT_PAGE.SAVE.SUCCESS'),
          this.objectSubtype$,
        ),
      )
      .subscribe({
        next: ([updatedValue, success, objectSubtype]) => {
          if (updatedValue === null) {
            return;
          }
          this.crmObjectManagementTableChangesService.addUpdatedCrmObject(objectType, updatedValue, objectSubtype);
          this.trackingService.trackEvent(objectType, 'edited-crm-object');
          this.snackService.openSuccessSnack(success, {
            duration: SNACKBAR_DURATION,
          });
        },
        error: (error) => {
          console.error(error);
          this.trackingService.trackEvent(objectType, 'failed-crm-edit');
          this.errorSnackbar(error, 'ERRORS.GENERIC_EDIT_MESSAGE');
        },
      });
  }

  private errorSnackbar(error: any | null, defaultMessage: string) {
    let message: string | null = null;
    if (error && error.message) {
      message = error.message;
    }
    this.snackService.openErrorSnack(message ?? defaultMessage, {
      duration: SNACKBAR_DURATION,
    });
  }
}
